import React, { memo, type ComponentType } from 'react';
import { useFragment, graphql } from 'react-relay';
import { MenuSection, MenuSectionHeading, MenuList } from '@atlaskit/navigation-system';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import LinkMenuItem from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import type { DelegatedAdminJiraSection$key } from '@atlassian/jira-relay/src/__generated__/DelegatedAdminJiraSection.graphql';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';
import messages from './messages.tsx';

type Props = {
	queryReference?: DelegatedAdminJiraSection$key;
};

const DelegatedAdminJiraSectionInternal = ({ queryReference }: Props) => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();

	// TODO: update permission check to use MANAGE_CUSTOM_ONBOARDING [PNDR-4560]
	const fragmentData = useFragment<DelegatedAdminJiraSection$key>(
		graphql`
			fragment DelegatedAdminJiraSection on Query
			@argumentDefinitions(
				cloudId: { type: "ID!" }
				skipDelegatedAdminCheck: { type: "Boolean!" }
			) {
				jira @skip(if: $skipDelegatedAdminCheck) {
					canManageCustomOnboarding: hasGlobalPermission(
						cloudId: $cloudId
						key: MANAGE_CUSTOM_ONBOARDING
					) @optIn(to: "JiraHasGlobalPermission")
				}
			}
		`,
		queryReference,
	);

	if (!fragmentData?.jira?.canManageCustomOnboarding) {
		return null;
	}

	return (
		<JSErrorBoundary
			id="delegated-admin-jira-section"
			packageName="jiraNavigationAppsSidebarNav4SidebarsSettingsGlobalProducts"
			fallback="flag"
		>
			<MenuSection isMenuListItem={fg('platform_design_system_nav4_sidenav_a11y')}>
				<MenuSectionHeading>{formatMessage(messages.heading)}</MenuSectionHeading>
				<MenuList>
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'onboardingHub' }}
						isSelected={[
							'products-onboarding-hub',
							'products-onboarding-hub-create',
							'products-onboarding-hub-edit',
						].includes(currentRoute.name)}
						href="/jira/settings/products/onboarding"
					>
						{formatMessage(messages.onboardingHubItem)}
					</LinkMenuItem>
				</MenuList>
			</MenuSection>
		</JSErrorBoundary>
	);
};

export const DelegatedAdminJiraSection: ComponentType<Props> = memo(
	DelegatedAdminJiraSectionInternal,
);
